import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { toTitleCase } from '../lib/format'
import SongRail from '../components/SongRail'
import { artGradient } from '../lib/songColour'
import { Shell } from '../components/Shell'
import { Spinner, Player, initials, avatarTone, personWash } from '../components/ui'
import FollowButton from '../components/FollowButton'

const STATE_NAMES = {
  NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', WA: 'Western Australia',
  SA: 'South Australia', TAS: 'Tasmania', ACT: 'Australian Capital Territory', NT: 'Northern Territory',
}

/**
 * Another writer's profile — what they've let out into the Collective, plus
 * whoever they are willing to be seen by. RLS already shapes the songs and
 * clips lists to what this viewer can reach; this screen adds no filter of
 * its own on top of that.
 *
 * Deliberately doesn't show song_count from songwriter_profiles — that view
 * counts private drafts too, and this page only ever lists what it can
 * actually show, so the two numbers would disagree in a way that leaks how
 * much unfinished work someone's sitting on.
 *
 * Follower counts don't appear here at all (only on /me — see Me.jsx). Who
 * follows a *stranger* is nobody else's business; the stat row instead counts
 * things the viewer themselves can already see if they scroll down.
 */
const writerName = (u) => [u?.first_name, u?.last_name].filter(Boolean).join(' ')

/** One horizontally-scrolling clip tile, portrait — the feed underneath this
 *  profile is vertical video, so a 9:16 tile is the honest shape for it. */
function ClipTile({ clip, title, byline, playing, loading, onPlay }) {
  return (
    <div className="wp-reel">
      {/* The clip's own song colour, same generator as the cards below it —
          an unplayed clip was a black rectangle with a play button in it,
          which read as a broken image next to a rail of coloured artwork.
          Cleared once the player mounts so the poster isn't tinted. */}
      <div className="wp-reel-frame"
           style={playing !== undefined ? undefined
                                        : { background: artGradient(clip.song.id) }}>
        {playing !== undefined
          ? <Player src={playing} mime="video/mp4" duration={clip.duration_seconds} />
          : (
            <button className="wp-reel-play" onClick={onPlay} disabled={loading} aria-label="Play">
              {loading ? '···' : '▶'}
            </button>
          )}
      </div>
      <p className="wp-reel-title">{title}</p>
      {byline}
    </div>
  )
}

export default function WriterProfile() {
  const { id } = useParams()
  const { user } = useSession()
  const mine = user?.id === id

  const [writer, setWriter] = useState(null)
  const [avatarUrl, setAvatarUrl] = useState(null)
  const [clips, setClips] = useState([])   // their own songs, out in the Collective
  const [songs, setSongs] = useState([])
  const [covers, setCovers] = useState([]) // their covers of other people's songs
  const [ready, setReady] = useState(false)
  const [err, setErr] = useState(null)
  const [clipSrc, setClipSrc] = useState({})
  // The churches that have chosen to put this writer on their own page. Not
  // "their church" — membership is private, and this is only the half the
  // church itself has published.
  const [backing, setBacking] = useState([])
  const [loadingClip, setLoadingClip] = useState(null)

  const collectiveRef = useRef(null)
  const songsRef = useRef(null)
  const coversRef = useRef(null)

  const load = useCallback(async () => {
    const [{ data: w }, { data: cl }, { data: sg }, { data: cv }] = await Promise.all([
      supabase.from('songwriter_profiles').select('*').eq('id', id).maybeSingle(),
      supabase.from('song_clips')
        .select('id, duration_seconds, song:song_id(id, title)')
        .eq('owner_id', id).eq('kind', 'original').is('removed_at', null),
      supabase.from('songs')
        .select('id, title, scripture_refs').eq('songwriter_id', id),
      supabase.from('song_clips')
        .select(`id, duration_seconds, created_at,
                 song:song_id(id, title, songwriter_id,
                              writer:songwriter_id(first_name, last_name))`)
        .eq('owner_id', id).eq('kind', 'cover').is('removed_at', null)
        .order('created_at', { ascending: false }),
    ])
    const { data: feat } = await supabase.from('church_features')
      .select('church_id').eq('writer_id', id).eq('kind', 'writer')
    const churchIds = [...new Set((feat ?? []).map((f) => f.church_id))]
    if (churchIds.length) {
      const { data: ch } = await supabase.from('church_profiles')
        .select('id, name').in('id', churchIds)
      setBacking(ch ?? [])
    } else {
      setBacking([])
    }

    setWriter(w ?? null)
    setClips((cl ?? []).filter((c) => c.song))
    setSongs(sg ?? [])
    setCovers((cv ?? []).filter((c) => c.song))

    // Avatar precedence: an upload (avatar_object_key) beats Google's
    // avatar_url beats the initials circle. See Me.jsx for the same call.
    if (w?.avatar_object_key) {
      const { data } = await supabase.functions.invoke('r2-avatar-url', {
        body: { mode: 'read', user_id: id },
      })
      setAvatarUrl(data?.url ?? null)
    } else {
      setAvatarUrl(w?.avatar_url ?? null)
    }
    setReady(true)
  }, [id])

  useEffect(() => { load() }, [load])

  const playClip = async (clipId) => {
    if (clipSrc[clipId]) return
    setLoadingClip(clipId)
    const { data, error } = await supabase.functions
      .invoke('r2-playback-url', { body: { clip_id: clipId } })
    setLoadingClip(null)
    if (error) return setErr(readableError(error))
    setClipSrc((m) => ({ ...m, [clipId]: data?.url ?? null }))
  }

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  if (!ready) return <Shell back="/collective" tabs><Spinner /></Shell>

  if (!writer) {
    return (
      <Shell back="/collective" tabs>
        <h2 className="q">Can't find this writer.</h2>
        <p className="sub">They may have deleted their account.</p>
      </Shell>
    )
  }

  const name = [writer.first_name, writer.last_name].filter(Boolean).join(' ')

  return (
    <Shell back="/collective" tabs>
      {/* The wash is the writer's own colour, cradling their face — see
          lib/songColour. Everything readable sits below it on the ordinary
          surface, which is what keeps this from being a contrast problem:
          the only thing over the colour is a photograph. */}
      <header className="wp-head">
        <div className="wp-wash" style={{ background: personWash(writer.id) }}
             aria-hidden="true" />
        {avatarUrl
          ? <img className="wp-avatar-lg" src={avatarUrl} alt="" />
          : <div className={`wp-avatar-lg ${avatarTone(writer.id)}`}>{initials(name)}</div>}
        <h2 className="wp-name">{name || 'Writer'}</h2>
        {writer.tagline && <p className="wp-tagline">{writer.tagline}</p>}
        {writer.state && STATE_NAMES[writer.state] && (
          <p className="wp-place">{STATE_NAMES[writer.state]}</p>
        )}
        {writer.bio && <p className="wp-bio">{writer.bio}</p>}
        {/* One primary action. There is no "Message" here because there is no
            messaging — a second button that goes nowhere is an affordance
            promising something the app can't do. */}
        <div className="wp-act">
          {mine
            ? <Link className="btn btn-quiet" to="/me">Your profile</Link>
            : <FollowButton writerId={writer.id} prominent />}
        </div>
        {backing.map((c) => (
          <Link key={c.id} className="wp-backing" to={`/church/${c.id}`}>
            Featured by {c.name} ›
          </Link>
        ))}
      </header>

      <div className="wp-stats">
        <button className="wp-stat" onClick={() => scrollTo(songsRef)}>
          <b>{songs.length}</b><span>Songs</span>
        </button>
        <button className="wp-stat" onClick={() => scrollTo(collectiveRef)}>
          <b>{clips.length}</b><span>In the Collective</span>
        </button>
        <button className="wp-stat" onClick={() => scrollTo(coversRef)}>
          <b>{covers.length}</b><span>Covers</span>
        </button>
      </div>

      {/* "View all" only where there is somewhere to view them. The Collective
          is a real destination; a writer's catalogue and covers are not, so
          those heads stay bare rather than growing a link to nowhere. */}
      <div className="wp-sec">
        <h3 ref={collectiveRef} className="sechead">In the Collective</h3>
        {/* Only when there is something to view all of — "View all" over
            "Nothing out here yet" is a link arguing with the sentence
            underneath it. */}
        {clips.length > 0 && (
          <Link className="wp-sec-all" to="/collective">View all ›</Link>
        )}
      </div>
      {clips.length === 0 ? (
        <p className="small">Nothing out here yet — their first clip will show up in this row.</p>
      ) : (
        <div className="wp-strip">
          {clips.map((c) => (
            <ClipTile key={c.id} clip={c} title={toTitleCase(c.song.title) || 'Untitled'}
                      loading={loadingClip === c.id} playing={clipSrc[c.id]}
                      onPlay={() => playClip(c.id)} />
          ))}
        </div>
      )}

      <h3 ref={songsRef} className="sechead">Songs</h3>
      {songs.length === 0 ? (
        <p className="small">Nothing you can hear yet.</p>
      ) : (
        // Cards in a rail, not .action rows. A catalogue rendered in the same
        // grey rectangle as a settings link reads as settings; a song wants
        // its artwork, and lib/songColour gives every song one for free.
        <SongRail songs={songs} to={(s) => `/collective/${s.id}`}
                  meta={(s) => s.scripture_refs?.join(' · ')} />
      )}

      <h3 ref={coversRef} className="sechead">Covers</h3>
      {covers.length === 0 ? (
        <p className="small">
          No covers yet — recording someone else's song and sending it back is
          how writers here end up meeting each other.
        </p>
      ) : (
        <div className="wp-strip">
          {covers.map((c) => (
            <ClipTile key={c.id} clip={c} title={toTitleCase(c.song.title) || 'Untitled'}
                      loading={loadingClip === c.id} playing={clipSrc[c.id]}
                      onPlay={() => playClip(c.id)}
                      byline={
                        <p className="wp-reel-by">
                          Written by{' '}
                          <Link className="linkish" to={`/writer/${c.song.songwriter_id}`}>
                            {writerName(c.song.writer) || 'another writer'}
                          </Link>
                        </p>
                      } />
          ))}
        </div>
      )}

      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
